'use client';

import { CircularProgress, Stack, Typography } from '@mui/material';
import type { Connector } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { OptionMenu } from '@/components/option-menu/option-menu';
import { PanelWrapper } from '@/components/panels/panelWrapper';
import { useDAL } from '@/contexts/useDAL';
import { useRoles } from '@/contexts/useRoles';
import { dayjs } from '@/utils/dayjs';

import { ConnectorStatusCell } from './connectorStatusCell';
import { DeleteConnectorDialog } from './deleteConnectorDialog';

interface ConnectorPanelProps {
  id: string;
  workspaceId: string;
  onClose: () => void;
  onRefresh: () => void;
}

export const ConnectorPanel = ({
  id,
  workspaceId,
  onClose,
  onRefresh,
}: ConnectorPanelProps) => {
  const dal = useDAL();
  const { isAuthorized } = useRoles();
  const [isLoading, setIsLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const { data: connectorsData, isLoading: isDataLoading } =
    dal.lakehouse.useListConnectors(workspaceId);

  const connector = connectorsData?.connectors?.find(
    (c: Connector) => c.id === id
  );

  if (isDataLoading || !connector) {
    return (
      <PanelWrapper header={{ title: 'Connector Details', onClose }}>
        <Stack alignItems='center' justifyContent='center' flex={1}>
          <CircularProgress />
        </Stack>
      </PanelWrapper>
    );
  }

  const canUpdate = isAuthorized('update:connector');
  const canDelete = isAuthorized('delete:connector');
  const isActive = connector.status === 'ACTIVE';

  const handleActivate = async () => {
    setIsLoading(true);
    try {
      await dal.lakehouse.activateConnector(workspaceId, connector.id);
      onRefresh();
    } catch (err) {
      console.error('Failed to activate connector:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeactivate = async () => {
    setIsLoading(true);
    try {
      await dal.lakehouse.deactivateConnector(workspaceId, connector.id);
      onRefresh();
    } catch (err) {
      console.error('Failed to deactivate connector:', err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <PanelWrapper
        header={{
          title: 'Connector Details',
          onClose,
          actions: isLoading ? (
            <CircularProgress size={20} />
          ) : (
            <OptionMenu
              options={[
                {
                  id: 'activate',
                  text: 'Activate',
                  icon: 'material-symbols-check-circle-outline-rounded',
                  menuItemProps: {
                    disabled: !canUpdate || isActive,
                    onClick: () => void handleActivate(),
                  },
                },
                {
                  id: 'deactivate',
                  text: 'Deactivate',
                  icon: 'material-symbols-do-not-disturb-on-outline-rounded',
                  menuItemProps: {
                    disabled: !canUpdate || !isActive,
                    onClick: () => void handleDeactivate(),
                  },
                },
                {
                  id: 'divider',
                  divider: true,
                },
                {
                  id: 'delete',
                  text: 'Delete',
                  icon: 'material-symbols-delete-outline-rounded',
                  menuItemProps: {
                    disabled: !canDelete,
                    onClick: () => setDeleteDialogOpen(true),
                  },
                },
              ]}
            />
          ),
        }}
      >
        <Stack gap='12px' padding='24px 40px'>
          <DetailRow label='ID' value={connector.id} />
          <DetailRow label='Name' value={connector.name} />
          <DetailRow
            label='Type'
            value={
              connector.connectorType === 'AWS_ACCOUNT_CONNECTOR'
                ? 'AWS Account'
                : connector.connectorType === 'GCP_PROJECT_CONNECTOR'
                  ? 'GCP Project'
                  : connector.connectorType
            }
          />
          <DetailRow
            label='Status'
            value={<ConnectorStatusCell status={connector.status} />}
          />
          {connector.connectorType === 'AWS_ACCOUNT_CONNECTOR' && (
            <>
              <DetailRow
                label='AWS Account ID'
                value={connector.connectorExternalId}
              />
              <DetailRow
                label='Discovery Role'
                value={connector.awsAccountProperties?.discoveryRoleName}
              />
              <DetailRow
                label='Data Access Role'
                value={connector.awsAccountProperties?.dataAccessRoleName}
              />
            </>
          )}
          {connector.connectorType === 'GCP_PROJECT_CONNECTOR' && (
            <>
              <DetailRow
                label='GCP Project ID'
                value={connector.connectorExternalId}
              />
              <DetailRow
                label='Discovery Service Account'
                value={
                  connector.gcpProjectProperties?.discoveryServiceAccountName
                }
              />
              <DetailRow
                label='Data Access Service Account'
                value={
                  connector.gcpProjectProperties?.dataAccessServiceAccountName
                }
              />
            </>
          )}
          <DetailRow
            label='Created'
            value={dayjs
              .utc(connector.createdAt)
              .format('MMM D, YYYY HH:mm:ss')}
          />
        </Stack>
      </PanelWrapper>

      <DeleteConnectorDialog
        connector={connector}
        workspaceId={workspaceId}
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onDeleted={() => {
          onRefresh();
          onClose();
        }}
      />
    </>
  );
};

const DetailRow = ({
  label,
  value,
}: {
  label: string;
  value?: React.ReactNode;
}) => (
  <Stack direction='row' alignItems='flex-start' gap='16px'>
    <Typography
      variant='body2'
      color='text.secondary'
      sx={{ minWidth: '200px', flexShrink: 0 }}
    >
      {label}
    </Typography>
    {typeof value === 'string' ? (
      <Typography variant='body2' sx={{ wordBreak: 'break-word', flex: 1 }}>
        {value || '-'}
      </Typography>
    ) : (
      (value ?? (
        <Typography variant='body2' sx={{ flex: 1 }}>
          -
        </Typography>
      ))
    )}
  </Stack>
);
